import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserX } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { fmtRelative } from '../lib/utils';

interface StuckSignup {
  token: string;
  created_at: string;
  status: 'pending' | 'expired' | 'failed';
  failure_reason: string | null;
  name: string | null;
  role: 'business' | 'courier' | null;
  vehicle_mode: string | null;
  business_name: string | null;
  contact_phone: string | null;
  attempts: number;
  existing_user_id: string | null;
}

export function StuckSignupsPage() {
  const [rows, setRows] = useState<StuckSignup[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showRecovered, setShowRecovered] = useState(false);

  useEffect(() => {
    api<{ signups: StuckSignup[] }>('/api/admin/stuck-signups')
      .then((r) => setRows(r.signups))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }, []);

  const recoveredCount = rows?.filter((r) => r.existing_user_id).length ?? 0;
  const visible = rows?.filter((r) => showRecovered || !r.existing_user_id);

  return (
    <Shell title="Stuck signups">
      <PageHeader
        title="Stuck signups"
        description="Signups from the last 90 days that never finished Telegram/WhatsApp verification. The phone is self-reported and unverified."
      />

      {recoveredCount > 0 && (
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={() => setShowRecovered((v) => !v)}>
            {showRecovered
              ? 'Hide people who signed up later'
              : `Show ${recoveredCount} who signed up later`}
          </Button>
        </div>
      )}

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows && !err && Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}><CardContent className="p-4"><Skeleton className="h-12 w-full" /></CardContent></Card>
        ))}
        {visible && visible.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState icon={UserX} title="No stuck signups." />
            </CardContent>
          </Card>
        )}
        {visible?.map((r) => (
          <Card key={r.token}>
            <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-text">{r.name ?? '—'}</span>
                  {r.role && <Badge>{r.role === 'courier' ? 'Delivery partner' : 'Business'}</Badge>}
                  {r.attempts > 1 && <Badge variant="urgent">{r.attempts} attempts</Badge>}
                  {r.existing_user_id && (
                    <Link to={`/users/${r.existing_user_id}`}>
                      <Badge variant="completed">Signed up later</Badge>
                    </Link>
                  )}
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  {[r.business_name, r.vehicle_mode, r.failure_reason ?? (r.status === 'failed' ? 'failed' : 'expired')]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="shrink-0 text-sm">
                {r.contact_phone ? (
                  <a
                    href={`https://wa.me/${r.contact_phone.replace(/^\+/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tnum font-medium text-brand-700 hover:underline dark:text-brand-500"
                  >
                    {r.contact_phone}
                  </a>
                ) : (
                  <span className="text-text-muted">No phone</span>
                )}
              </div>
              <span className="shrink-0 text-xs text-text-muted md:w-24 md:text-right">
                {fmtRelative(r.created_at)}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
    </Shell>
  );
}
